import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Activity, ArrowLeft, Clock, DollarSign, Mic2, Sparkles, Volume2 } from "lucide-react";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import { EmptyState } from "@/components/states/EmptyState";

type Range = "24h" | "7d" | "30d";

interface Intervention {
  id: string;
  session_id: string;
  created_at: string;
  e2e_ttfb_ms: number | null;
  stt_latency_ms: number | null;
  llm_latency_total_ms: number | null;
  llm_latency_first_token_ms: number | null;
  tts_latency_first_byte_ms: number | null;
  llm_tokens_in: number | null;
  llm_tokens_out: number | null;
  llm_model: string | null;
  stt_provider: string | null;
  tts_provider: string | null;
  tts_characters: number | null;
  was_interrupted: boolean;
  cost_usd: number;
  trigger_type: string;
}

interface Session {
  id: string;
  created_at: string;
  ended_at: string | null;
  status: string;
  mode: string;
  total_turns: number;
  total_cost_usd: number;
  total_llm_tokens: number;
  total_stt_ms: number;
  total_tts_chars: number;
}

const rangeToHours = (r: Range) => (r === "24h" ? 24 : r === "7d" ? 168 : 720);

function pct(arr: number[], p: number): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
}

function fmtMs(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`;
  return `${Math.round(n)}ms`;
}

function fmtUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}

const chartConfig: ChartConfig = {
  e2e: { label: "E2E", color: "hsl(var(--primary))" },
  stt: { label: "STT", color: "hsl(var(--chart-2, 200 80% 55%))" },
  llm: { label: "LLM", color: "hsl(var(--chart-3, 280 70% 60%))" },
  tts: { label: "TTS", color: "hsl(var(--chart-4, 30 90% 55%))" },
  cost: { label: "Custo", color: "hsl(var(--primary))" },
};

export default function JarvisTelemetry() {
  const [range, setRange] = useState<Range>("24h");
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - rangeToHours(range) * 3600 * 1000).toISOString();

      const [iv, ss] = await Promise.all([
        supabase
          .from("jarvis_interventions")
          .select(
            "id,session_id,created_at,e2e_ttfb_ms,stt_latency_ms,llm_latency_total_ms,llm_latency_first_token_ms,tts_latency_first_byte_ms,llm_tokens_in,llm_tokens_out,llm_model,stt_provider,tts_provider,tts_characters,was_interrupted,cost_usd,trigger_type",
          )
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(1000),
        supabase
          .from("jarvis_sessions")
          .select("id,created_at,ended_at,status,mode,total_turns,total_cost_usd,total_llm_tokens,total_stt_ms,total_tts_chars")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(200),
      ]);

      if (cancelled) return;
      setInterventions((iv.data ?? []) as Intervention[]);
      setSessions((ss.data ?? []) as Session[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const kpis = useMemo(() => {
    const e2e = interventions.map((i) => i.e2e_ttfb_ms ?? 0).filter((n) => n > 0);
    const stt = interventions.map((i) => i.stt_latency_ms ?? 0).filter((n) => n > 0);
    const llm = interventions.map((i) => i.llm_latency_total_ms ?? 0).filter((n) => n > 0);
    const tts = interventions.map((i) => i.tts_latency_first_byte_ms ?? 0).filter((n) => n > 0);
    const totalCost = interventions.reduce((s, i) => s + Number(i.cost_usd ?? 0), 0);
    const totalTokens = interventions.reduce((s, i) => s + (i.llm_tokens_in ?? 0) + (i.llm_tokens_out ?? 0), 0);
    const interrupted = interventions.filter((i) => i.was_interrupted).length;
    return {
      turns: interventions.length,
      sessions: sessions.length,
      e2eP50: pct(e2e, 50),
      e2eP95: pct(e2e, 95),
      sttP50: pct(stt, 50),
      llmP50: pct(llm, 50),
      ttsP50: pct(tts, 50),
      totalCost,
      totalTokens,
      interruptionRate: interventions.length ? (interrupted / interventions.length) * 100 : 0,
      avgCostPerSession: sessions.length ? totalCost / sessions.length : 0,
    };
  }, [interventions, sessions]);

  const timeSeries = useMemo(() => {
    const buckets = new Map<string, { ts: string; e2e: number[]; stt: number[]; llm: number[]; tts: number[]; cost: number }>();
    const bucketSize = range === "24h" ? 3600_000 : 24 * 3600_000; // 1h or 1d
    for (const i of interventions) {
      const t = new Date(i.created_at).getTime();
      const bucket = Math.floor(t / bucketSize) * bucketSize;
      const key = new Date(bucket).toISOString();
      const b = buckets.get(key) ?? { ts: key, e2e: [], stt: [], llm: [], tts: [], cost: 0 };
      if (i.e2e_ttfb_ms) b.e2e.push(i.e2e_ttfb_ms);
      if (i.stt_latency_ms) b.stt.push(i.stt_latency_ms);
      if (i.llm_latency_total_ms) b.llm.push(i.llm_latency_total_ms);
      if (i.tts_latency_first_byte_ms) b.tts.push(i.tts_latency_first_byte_ms);
      b.cost += Number(i.cost_usd ?? 0);
      buckets.set(key, b);
    }
    return Array.from(buckets.values())
      .sort((a, b) => a.ts.localeCompare(b.ts))
      .map((b) => ({
        label:
          range === "24h"
            ? new Date(b.ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
            : new Date(b.ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        e2e: Math.round(pct(b.e2e, 50)),
        stt: Math.round(pct(b.stt, 50)),
        llm: Math.round(pct(b.llm, 50)),
        tts: Math.round(pct(b.tts, 50)),
        cost: Number(b.cost.toFixed(4)),
      }));
  }, [interventions, range]);

  const providerBreakdown = useMemo(() => {
    const map = new Map<string, { provider: string; count: number; latency: number[] }>();
    for (const i of interventions) {
      if (!i.llm_model) continue;
      const k = i.llm_model;
      const e = map.get(k) ?? { provider: k, count: 0, latency: [] };
      e.count++;
      if (i.llm_latency_total_ms) e.latency.push(i.llm_latency_total_ms);
      map.set(k, e);
    }
    return Array.from(map.values())
      .map((e) => ({ provider: e.provider, count: e.count, p50: Math.round(pct(e.latency, 50)) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [interventions]);

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-7xl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/jarvis">
                <ArrowLeft className="size-4 mr-1" /> Voltar
              </Link>
            </Button>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Telemetria Jarvis</h1>
          <p className="text-muted-foreground text-sm">Latência por turno, custo por sessão e breakdown STT / LLM / TTS.</p>
        </div>
        <Select value={range} onValueChange={(v) => setRange(v as Range)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Últimas 24h</SelectItem>
            <SelectItem value="7d">Últimos 7 dias</SelectItem>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
          </SelectContent>
        </Select>
      </header>

      {loading ? (
        <LoadingSkeleton />
      ) : interventions.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Nenhuma intervenção no período"
          description="Inicie uma sessão Jarvis para começar a coletar telemetria."
        />
      ) : (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={Activity} label="Turnos" value={String(kpis.turns)} sub={`${kpis.sessions} sessões`} />
            <KpiCard icon={Clock} label="E2E p50 / p95" value={fmtMs(kpis.e2eP50)} sub={`p95 ${fmtMs(kpis.e2eP95)}`} />
            <KpiCard icon={DollarSign} label="Custo total" value={fmtUsd(kpis.totalCost)} sub={`${fmtUsd(kpis.avgCostPerSession)} / sessão`} />
            <KpiCard icon={Sparkles} label="Tokens LLM" value={kpis.totalTokens.toLocaleString("pt-BR")} sub={`${kpis.interruptionRate.toFixed(1)}% interrompidos`} />
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KpiCard icon={Mic2} label="STT p50" value={fmtMs(kpis.sttP50)} accent />
            <KpiCard icon={Sparkles} label="LLM p50" value={fmtMs(kpis.llmP50)} accent />
            <KpiCard icon={Volume2} label="TTS p50 (TTFB)" value={fmtMs(kpis.ttsP50)} accent />
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Latência por etapa (p50)</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[260px] w-full">
                  <ResponsiveContainer>
                    <LineChart data={timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} unit="ms" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line type="monotone" dataKey="stt" stroke="var(--color-stt)" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="llm" stroke="var(--color-llm)" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="tts" stroke="var(--color-tts)" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="e2e" stroke="var(--color-e2e)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Custo ao longo do tempo</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[260px] w-full">
                  <ResponsiveContainer>
                    <AreaChart data={timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Area type="monotone" dataKey="cost" stroke="var(--color-cost)" fill="var(--color-cost)" fillOpacity={0.25} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          </section>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Modelos LLM utilizados</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[220px] w-full">
                <ResponsiveContainer>
                  <BarChart data={providerBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="provider" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="var(--color-llm)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sessões recentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Início</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Modo</TableHead>
                      <TableHead className="text-right">Turnos</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Custo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.slice(0, 20).map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(s.created_at).toLocaleString("pt-BR")}
                        </TableCell>
                        <TableCell>
                          <Badge variant={s.status === "completed" ? "default" : s.status === "error" ? "destructive" : "secondary"}>
                            {s.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{s.mode}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.total_turns}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.total_llm_tokens?.toLocaleString("pt-BR") ?? 0}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtUsd(Number(s.total_cost_usd ?? 0))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <Card className={accent ? "border-primary/30" : ""}>
      <CardContent className="p-4 space-y-1">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
          <Icon className="size-3.5" />
          {label}
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}
